import { useState, useEffect, useMemo, useCallback } from 'react';
import Head from 'next/head';
import Layout from '@/components/Layout';
import { useAuth } from '@/lib/authContext';
import { supabase } from '@/lib/supabase';
import { useRouter } from 'next/router';
import toast from 'react-hot-toast';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

// ─── Icon helper ─────────────────────────────────────────
function Icon({ name, className }: { name: string; className?: string }) {
  return (
    <span className={cn('material-symbols-outlined', className)}>{name}</span>
  );
}

/* ─── Types ──────────────────────────────────────────── */

type AccionTipo = 'subir_documento' | 'crear_incidencia' | 'consulta_asistente';

interface RegistroActividad {
  id: string;
  fecha: Date;
  usuarioNombre: string;
  accion: AccionTipo;
  detalle: string;
  empresa: string;
  estado: string;
}

/* ─── Action config ──────────────────────────────────── */

const ACCION_CONFIG: Record<AccionTipo, {
  label: string;
  icon: string;
  badgeBg: string;
  badgeText: string;
}> = {
  subir_documento: {
    label: 'Documento subido',
    icon: 'upload_file',
    badgeBg: 'bg-amber-50 border-amber-200',
    badgeText: 'text-amber-700',
  },
  crear_incidencia: {
    label: 'Incidencia',
    icon: 'report_problem',
    badgeBg: 'bg-red-50 border-red-200',
    badgeText: 'text-red-700',
  },
  consulta_asistente: {
    label: 'Consulta IA',
    icon: 'smart_toy',
    badgeBg: 'bg-blue-50 border-blue-200',
    badgeText: 'text-blue-700',
  },
};

const FILTRO_ACCIONES: { value: string; label: string }[] = [
  { value: 'todos', label: 'Todas las acciones' },
  { value: 'subir_documento', label: 'Documento subido' },
  { value: 'crear_incidencia', label: 'Incidencia' },
  { value: 'consulta_asistente', label: 'Consulta IA' },
];

/* ─── Estado badge helper ────────────────────────────── */

function getEstadoStyle(estado: string): { bg: string; text: string; label: string } {
  switch (estado) {
    case 'pendiente':
      return { bg: 'bg-orange-50 border-orange-200', text: 'text-orange-700', label: 'Pendiente' };
    case 'procesado_ia':
      return { bg: 'bg-sky-50 border-sky-200', text: 'text-sky-700', label: 'Procesado IA' };
    case 'contabilizado':
      return { bg: 'bg-emerald-50 border-emerald-200', text: 'text-emerald-700', label: 'Contabilizado' };
    case 'incidencia':
      return { bg: 'bg-red-50 border-red-200', text: 'text-red-700', label: 'Incidencia' };
    case 'rechazado':
      return { bg: 'bg-red-50 border-red-200', text: 'text-red-700', label: 'Rechazado' };
    case 'nueva':
      return { bg: 'bg-orange-50 border-orange-200', text: 'text-orange-700', label: 'Nueva' };
    case 'en_revision':
      return { bg: 'bg-blue-50 border-blue-200', text: 'text-blue-700', label: 'En revisión' };
    case 'resuelta':
      return { bg: 'bg-emerald-50 border-emerald-200', text: 'text-emerald-700', label: 'Resuelta' };
    case 'cerrada':
      return { bg: 'bg-slate-50 border-slate-200', text: 'text-slate-600', label: 'Cerrada' };
    case 'completada':
      return { bg: 'bg-emerald-50 border-emerald-200', text: 'text-emerald-700', label: 'Completada' };
    default:
      return { bg: 'bg-slate-50 border-slate-200', text: 'text-slate-600', label: estado || '—' };
  }
}

/* ─── Component ──────────────────────────────────────── */

export default function RegistroAccesoPage() {
  const { user, loading } = useAuth();
  const router = useRouter();

  /* ── State ── */
  const [registros, setRegistros] = useState<RegistroActividad[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [filtroAccion, setFiltroAccion] = useState('todos');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  /* ── Auth guard ── */
  useEffect(() => {
    if (!loading && (!user || user.rol !== 'asesor')) {
      router.push('/');
    }
  }, [user, loading, router]);

  /* ── Data loading ── */
  const loadData = useCallback(async () => {
    if (!user?.id) return;

    try {
      setLoadingData(true);
      const entries: RegistroActividad[] = [];

      // Get assigned client IDs
      const { data: assignedClients, error: clientError } = await supabase
        .from('asesor_cliente')
        .select('cliente_id, clientes(razon_social, nombre_comercial)')
        .eq('asesor_id', user.id);

      if (clientError) throw clientError;

      const clientIds = assignedClients?.map((ac: { cliente_id: string }) => ac.cliente_id) || [];

      // 1. Fetch recent documents (last 50)
      if (clientIds.length > 0) {
        const { data: docs, error: docsError } = await supabase
          .from('documentos')
          .select('id, nombre, fecha_subida, estado, cliente_id, clientes(razon_social, nombre_comercial)')
          .in('cliente_id', clientIds)
          .order('fecha_subida', { ascending: false })
          .limit(50);

        if (docsError) throw docsError;

        for (const doc of docs || []) {
          const clienteInfo = doc.clientes as { nombre_comercial?: string; razon_social?: string } | null;
          const empresaNombre = clienteInfo?.nombre_comercial || clienteInfo?.razon_social || '—';

          entries.push({
            id: `doc-${doc.id}`,
            fecha: new Date(doc.fecha_subida),
            usuarioNombre: empresaNombre,
            accion: 'subir_documento',
            detalle: doc.nombre || 'Documento sin nombre',
            empresa: empresaNombre,
            estado: doc.estado || 'pendiente',
          });
        }
      }

      // 2. Fetch recent incidencias (last 20)
      if (clientIds.length > 0) {
        const { data: incidents, error: incError } = await supabase
          .from('incidencias')
          .select('id, estado, fecha_creacion, cliente_id, clientes(razon_social, nombre_comercial)')
          .in('cliente_id', clientIds)
          .order('fecha_creacion', { ascending: false })
          .limit(20);

        if (incError) throw incError;

        for (const inc of incidents || []) {
          const clienteInfo = inc.clientes as { nombre_comercial?: string; razon_social?: string } | null;
          const empresaNombre = clienteInfo?.nombre_comercial || clienteInfo?.razon_social || '—';

          entries.push({
            id: `inc-${inc.id}`,
            fecha: new Date(inc.fecha_creacion),
            usuarioNombre: empresaNombre,
            accion: 'crear_incidencia',
            detalle: `Incidencia — ${inc.estado || 'nueva'}`,
            empresa: empresaNombre,
            estado: inc.estado || 'nueva',
          });
        }
      }

      // 3. Fetch recent conversaciones del asistente (last 20)
      const { data: convs, error: convError } = await supabase
        .from('conversaciones_asistente')
        .select('id, titulo, fecha_creacion')
        .eq('asesor_id', user.id)
        .order('fecha_creacion', { ascending: false })
        .limit(20);

      if (convError) throw convError;

      const asesorNombre = user.nombre || 'Asesor';

      for (const conv of convs || []) {
        entries.push({
          id: `conv-${conv.id}`,
          fecha: new Date(conv.fecha_creacion),
          usuarioNombre: asesorNombre,
          accion: 'consulta_asistente',
          detalle: conv.titulo || 'Consulta sin título',
          empresa: '—',
          estado: 'completada',
        });
      }

      // Sort all entries by date descending
      entries.sort((a, b) => b.fecha.getTime() - a.fecha.getTime());
      setRegistros(entries);
    } catch (error) {
      console.error('Error al cargar registro de actividad:', error);
      toast.error('Error al cargar el registro de actividad');
    } finally {
      setLoadingData(false);
    }
  }, [user]);

  useEffect(() => {
    if (user && user.rol === 'asesor') {
      loadData();
    }
  }, [user, loadData]);

  /* ── Filtered data ── */
  const filteredData = useMemo(() => {
    let data = [...registros];

    // Filter by search term
    if (busqueda.trim()) {
      const term = busqueda.toLowerCase().trim();
      data = data.filter(
        (r) =>
          r.usuarioNombre.toLowerCase().includes(term) ||
          r.detalle.toLowerCase().includes(term) ||
          r.empresa.toLowerCase().includes(term)
      );
    }

    // Filter by action type
    if (filtroAccion !== 'todos') {
      data = data.filter((r) => r.accion === filtroAccion);
    }

    // Filter by date range
    if (fechaDesde) {
      const desde = new Date(fechaDesde);
      desde.setHours(0, 0, 0, 0);
      data = data.filter((r) => r.fecha >= desde);
    }
    if (fechaHasta) {
      const hasta = new Date(fechaHasta);
      hasta.setHours(23, 59, 59, 999);
      data = data.filter((r) => r.fecha <= hasta);
    }

    return data;
  }, [registros, busqueda, filtroAccion, fechaDesde, fechaHasta]);

  /* ── Stats ── */
  const stats = useMemo(() => {
    const totalDocumentos = registros.filter((r) => r.accion === 'subir_documento').length;
    const totalIncidencias = registros.filter((r) => r.accion === 'crear_incidencia').length;
    const totalConsultas = registros.filter((r) => r.accion === 'consulta_asistente').length;
    const ultimaActividad = registros.length > 0 ? registros[0].fecha : null;

    return {
      totalDocumentos,
      totalIncidencias,
      totalConsultas,
      ultimaActividad,
    };
  }, [registros]);

  /* ── Pagination ── */
  const totalPages = Math.max(1, Math.ceil(filteredData.length / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = filteredData.slice(startIndex, startIndex + itemsPerPage);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [currentPage, totalPages]);

  /* ── CSV Export ── */
  const exportarCSV = useCallback(() => {
    const headers = ['Fecha/Hora', 'Origen', 'Acción', 'Detalle', 'Empresa', 'Estado'];

    const rows = filteredData.map((r) =>
      [
        `"${r.fecha.toLocaleString('es-ES')}"`,
        `"${r.usuarioNombre.replace(/"/g, '""')}"`,
        `"${ACCION_CONFIG[r.accion].label}"`,
        `"${r.detalle.replace(/"/g, '""')}"`,
        `"${r.empresa.replace(/"/g, '""')}"`,
        `"${getEstadoStyle(r.estado).label}"`,
      ].join(',')
    );

    const csvContent = [headers.join(','), ...rows].join('\n');
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const date = new Date().toISOString().split('T')[0];
    link.href = url;
    link.download = `registro_actividad_${date}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success('CSV exportado correctamente');
  }, [filteredData]);

  /* ── Date formatting ── */
  const formatFecha = (fecha: Date): string => {
    return fecha.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatHora = (fecha: Date): string => {
    return fecha.toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const formatUltimaActividad = (fecha: Date | null): string => {
    if (!fecha) return '—';
    const ahora = new Date();
    const diffMs = ahora.getTime() - fecha.getTime();
    const diffMin = Math.floor(diffMs / 60000);

    if (diffMin < 1) return 'Hace un momento';
    if (diffMin < 60) return `Hace ${diffMin} min`;
    const diffHoras = Math.floor(diffMin / 60);
    if (diffHoras < 24) return `Hace ${diffHoras}h`;
    return formatFecha(fecha) + ' ' + formatHora(fecha);
  };

  /* ── Loading state ── */
  if (loading || !user || user.rol !== 'asesor') {
    return (
      <Layout rol="asesor">
        <div className="min-h-screen flex items-center justify-center bg-calm-bg">
          <div className="flex flex-col items-center gap-4">
            <Skeleton className="h-12 w-12 rounded-full" />
            <Skeleton className="h-4 w-40 rounded-huge" />
          </div>
        </div>
      </Layout>
    );
  }

  /* ── Render ── */
  return (
    <>
      <Head>
        <title>Registro de Actividad — Externaliza2</title>
        <link rel="icon" href="/logo.png" />
      </Head>
      <Layout rol="asesor">
        <div className="h-[calc(100vh-72px)] flex flex-col overflow-hidden bg-calm-bg">
          <div className="flex-1 overflow-y-auto">
            <div className="px-12 pt-12 pb-12 space-y-10">

              {/* ══════════ Page Header ══════════ */}
              <div className="flex items-end justify-between">
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-calm-accent mb-2">
                    Seguridad
                  </p>
                  <h1 className="font-outfit text-5xl font-light text-calm-text tracking-tight">
                    Registro de Actividad
                  </h1>
                  <p className="text-sm text-calm-muted mt-2 font-light">
                    Historial de actividad reciente en el sistema
                  </p>
                </div>

                <div className="flex items-center gap-3">
                  <button
                    onClick={loadData}
                    disabled={loadingData}
                    className="border border-calm-border rounded-huge px-5 py-2.5 text-sm font-light text-calm-muted hover:bg-white hover:text-calm-text hover:shadow-sm transition-all flex items-center gap-2 disabled:opacity-40"
                  >
                    <Icon name="refresh" className={cn('text-base', loadingData && 'animate-spin')} />
                    Actualizar
                  </button>
                  <button
                    onClick={exportarCSV}
                    disabled={registros.length === 0}
                    className="border border-calm-border rounded-huge px-5 py-2.5 text-sm font-light text-calm-muted hover:bg-white hover:text-calm-text hover:shadow-sm transition-all flex items-center gap-2 disabled:opacity-40"
                  >
                    <Icon name="download" className="text-base" />
                    Exportar CSV
                  </button>
                </div>
              </div>

              {/* ══════════ Stats Cards ══════════ */}
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
                {/* Documentos */}
                <div className="bg-gradient-to-br from-white to-fresh-sky-50/20 border border-fresh-sky-200/40 rounded-huge p-6">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center justify-center w-10 h-10 rounded-huge bg-fresh-sky-50">
                      <Icon name="upload_file" className="text-xl text-fresh-sky-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] uppercase tracking-widest text-calm-accent font-normal">
                        Documentos subidos
                      </p>
                      <p className="text-2xl font-light text-calm-text tabular-nums mt-0.5">
                        {loadingData ? '…' : stats.totalDocumentos}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Incidencias */}
                <div className="bg-gradient-to-br from-white to-amber-50/20 border border-amber-200/40 rounded-huge p-6">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center justify-center w-10 h-10 rounded-huge bg-amber-50">
                      <Icon name="report_problem" className="text-xl text-amber-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] uppercase tracking-widest text-calm-accent font-normal">
                        Incidencias
                      </p>
                      <p className="text-2xl font-light text-calm-text tabular-nums mt-0.5">
                        {loadingData ? '…' : stats.totalIncidencias}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Consultas IA */}
                <div className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge p-6">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center justify-center w-10 h-10 rounded-huge bg-steel-azure-50">
                      <Icon name="smart_toy" className="text-xl text-steel-azure-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] uppercase tracking-widest text-calm-accent font-normal">
                        Consultas IA
                      </p>
                      <p className="text-2xl font-light text-calm-text tabular-nums mt-0.5">
                        {loadingData ? '…' : stats.totalConsultas}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Última Actividad */}
                <div className="bg-gradient-to-br from-white to-slate-50 border border-slate-200/60 rounded-huge p-6">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center justify-center w-10 h-10 rounded-huge bg-slate-50">
                      <Icon name="schedule" className="text-xl text-slate-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] uppercase tracking-widest text-calm-accent font-normal">
                        Última Actividad
                      </p>
                      <p className="text-lg font-light text-calm-text mt-0.5">
                        {loadingData ? '…' : formatUltimaActividad(stats.ultimaActividad)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* ══════════ Filters ══════════ */}
              <div className="flex flex-col lg:flex-row items-stretch lg:items-center gap-3">
                {/* Date range: Desde */}
                <div className="flex items-center gap-2">
                  <label htmlFor="fecha-desde" className="text-sm text-calm-muted whitespace-nowrap font-light">
                    Desde
                  </label>
                  <div className="relative">
                    <Icon name="calendar_today" className="absolute left-3 top-1/2 -translate-y-1/2 text-calm-accent text-[16px] pointer-events-none" />
                    <Input
                      id="fecha-desde"
                      type="date"
                      value={fechaDesde}
                      onChange={(e) => { setFechaDesde(e.target.value); setCurrentPage(1); }}
                      className="pl-9 pr-3 py-2 text-sm font-light border-calm-border rounded-huge"
                    />
                  </div>
                </div>

                {/* Date range: Hasta */}
                <div className="flex items-center gap-2">
                  <label htmlFor="fecha-hasta" className="text-sm text-calm-muted whitespace-nowrap font-light">
                    Hasta
                  </label>
                  <div className="relative">
                    <Icon name="calendar_today" className="absolute left-3 top-1/2 -translate-y-1/2 text-calm-accent text-[16px] pointer-events-none" />
                    <Input
                      id="fecha-hasta"
                      type="date"
                      value={fechaHasta}
                      onChange={(e) => { setFechaHasta(e.target.value); setCurrentPage(1); }}
                      className="pl-9 pr-3 py-2 text-sm font-light border-calm-border rounded-huge"
                    />
                  </div>
                </div>

                {/* Search */}
                <div className="relative flex-1 max-w-xs">
                  <Icon name="search" className="absolute left-3 top-1/2 -translate-y-1/2 text-calm-accent text-[18px] pointer-events-none" />
                  <Input
                    type="text"
                    placeholder="Buscar actividad…"
                    value={busqueda}
                    onChange={(e) => { setBusqueda(e.target.value); setCurrentPage(1); }}
                    className="pl-10 border-calm-border rounded-huge text-sm font-light"
                  />
                </div>

                {/* Action type filter */}
                <Select
                  value={filtroAccion}
                  onValueChange={(v) => { setFiltroAccion(v); setCurrentPage(1); }}
                >
                  <SelectTrigger className="w-[200px] border-calm-border rounded-huge text-sm font-light">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FILTRO_ACCIONES.map((f) => (
                      <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* ══════════ Activity Table ══════════ */}
              <div className="bg-gradient-to-br from-white to-fresh-sky-50/20 border border-fresh-sky-200/40 rounded-huge overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="border-b border-calm-border hover:bg-transparent">
                      <TableHead className="p-4 text-[11px] uppercase tracking-widest text-calm-accent font-normal whitespace-nowrap">Fecha / Hora</TableHead>
                      <TableHead className="p-4 text-[11px] uppercase tracking-widest text-calm-accent font-normal whitespace-nowrap">Origen</TableHead>
                      <TableHead className="p-4 text-[11px] uppercase tracking-widest text-calm-accent font-normal whitespace-nowrap">Acción</TableHead>
                      <TableHead className="p-4 text-[11px] uppercase tracking-widest text-calm-accent font-normal whitespace-nowrap">Detalle</TableHead>
                      <TableHead className="p-4 text-[11px] uppercase tracking-widest text-calm-accent font-normal whitespace-nowrap">Estado</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loadingData ? (
                      Array.from({ length: 5 }).map((_, i) => (
                        <TableRow key={`skel-${i}`} className="border-b border-calm-border/50">
                          <TableCell className="p-4"><Skeleton className="h-4 w-24 rounded-huge" /></TableCell>
                          <TableCell className="p-4"><Skeleton className="h-4 w-32 rounded-huge" /></TableCell>
                          <TableCell className="p-4"><Skeleton className="h-5 w-28 rounded-full" /></TableCell>
                          <TableCell className="p-4"><Skeleton className="h-4 w-40 rounded-huge" /></TableCell>
                          <TableCell className="p-4"><Skeleton className="h-5 w-20 rounded-full" /></TableCell>
                        </TableRow>
                      ))
                    ) : paginatedData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-16">
                          <div className="flex flex-col items-center gap-3">
                            <Icon name="shield" className="text-[32px] text-calm-accent" />
                            <span className="text-sm font-light text-calm-muted">
                              No se encontraron registros de actividad
                            </span>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedData.map((registro) => {
                        const config = ACCION_CONFIG[registro.accion];
                        const estadoStyle = getEstadoStyle(registro.estado);
                        return (
                          <TableRow
                            key={registro.id}
                            className="border-b border-calm-border/50 last:border-b-0 hover:bg-fresh-sky-50/30 transition-colors"
                          >
                            {/* Fecha / Hora */}
                            <TableCell className="p-4 whitespace-nowrap">
                              <div className="text-sm font-light text-calm-text tabular-nums">
                                {formatFecha(registro.fecha)}
                              </div>
                              <div className="text-[11px] text-calm-accent tabular-nums font-light">
                                {formatHora(registro.fecha)}
                              </div>
                            </TableCell>

                            {/* Origen */}
                            <TableCell className="p-4">
                              <div className="flex items-center gap-2">
                                <Icon
                                  name={registro.accion === 'consulta_asistente' ? 'smart_toy' : 'business'}
                                  className="text-[16px] text-calm-accent"
                                />
                                <span className="text-sm font-light text-calm-text truncate max-w-[180px]" title={registro.usuarioNombre}>
                                  {registro.usuarioNombre}
                                </span>
                              </div>
                            </TableCell>

                            {/* Acción badge */}
                            <TableCell className="p-4">
                              <Badge
                                variant="outline"
                                className={cn('gap-1.5 rounded-full text-[10px]', config.badgeBg, config.badgeText)}
                              >
                                <Icon name={config.icon} className="text-[13px]" />
                                {config.label}
                              </Badge>
                            </TableCell>

                            {/* Detalle */}
                            <TableCell className="p-4 text-calm-muted font-light text-sm max-w-[260px] truncate" title={registro.detalle}>
                              {registro.detalle}
                            </TableCell>

                            {/* Estado */}
                            <TableCell className="p-4">
                              <Badge
                                variant="outline"
                                className={cn('rounded-full text-[10px]', estadoStyle.bg, estadoStyle.text)}
                              >
                                {estadoStyle.label}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* ══════════ Pagination ══════════ */}
              <div className="flex items-center justify-end gap-4 text-sm font-light text-calm-muted">
                <div className="flex items-center gap-2">
                  <span className="whitespace-nowrap">Registros por página:</span>
                  <Select
                    value={String(itemsPerPage)}
                    onValueChange={(v) => { setItemsPerPage(Number(v)); setCurrentPage(1); }}
                  >
                    <SelectTrigger className="w-[70px] h-8 border-calm-border rounded-huge text-sm font-light">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="25">25</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <span className="tabular-nums whitespace-nowrap">
                  {filteredData.length === 0
                    ? '0 de 0'
                    : `${startIndex + 1}-${Math.min(startIndex + itemsPerPage, filteredData.length)} de ${filteredData.length}`
                  }
                </span>

                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage <= 1}
                    className="border border-calm-border rounded-huge w-8 h-8 flex items-center justify-center text-calm-muted hover:bg-white hover:text-calm-text transition-all disabled:opacity-40"
                    aria-label="Página anterior"
                  >
                    <Icon name="chevron_left" className="text-base" />
                  </button>
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage >= totalPages}
                    className="border border-calm-border rounded-huge w-8 h-8 flex items-center justify-center text-calm-muted hover:bg-white hover:text-calm-text transition-all disabled:opacity-40"
                    aria-label="Página siguiente"
                  >
                    <Icon name="chevron_right" className="text-base" />
                  </button>
                </div>
              </div>

            </div>
          </div>
        </div>
      </Layout>
    </>
  );
}
